import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { StatusPill } from '../components/StatusPill';
import { FLAG_TYPES, formatCentsShort, type Case } from '../data/sampleData';
import { api } from '../lib/api';
import { UnreadFbcUpdates } from '../components/FbcUpdates';

interface DashboardProps {
  cases: Case[];
  onOpen: (id: string) => void;
  onTrigger: () => void;
  onJumpToQueue: () => void;
}

function Sparkline({ data, color = 'var(--accent)', height = 32, width = 120 }: { data: number[]; color?: string; height?: number; width?: number }) {
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * width},${height - ((v - min) / range) * (height - 4) - 2}`).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <polyline points={`0,${height} ${pts} ${width},${height}`} fill={color} fillOpacity="0.08" stroke="none" />
    </svg>
  );
}

function RunTimeline({ series }: { series: DashboardSummary['series_14d'] }) {
  const days = series ?? [];
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {days.map((day, d) => {
        const today = d === days.length - 1;
        const total = day.runs_complete + day.runs_failed;
        return (
          <div key={day.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minHeight: 60 }}>
              {Array.from({ length: Math.max(total, 1) }).map((_, r) => {
                const failed = r < day.runs_failed;
                const sev = total === 0 ? 'info' : failed ? 'danger' : 'ok';
                return (
                  <div key={r} style={{
                    height: total === 0 ? 4 : 18,
                    background: total === 0 ? 'var(--surface-2)' : `var(--${sev}-soft)`,
                    borderLeft: total === 0 ? 'none' : `3px solid var(--${sev})`,
                    borderRadius: 3,
                  }} />
                );
              })}
            </div>
            <div style={{ fontSize: 10, color: today ? 'var(--accent)' : 'var(--text-faint)', textAlign: 'center', fontWeight: today ? 600 : 400, fontFamily: 'var(--font-mono)' }}>
              {today ? 'Today' : `−${days.length - 1 - d}d`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface RecentAction {
  id: number;
  action: string;
  target_type: string | null;
  target_id: string | null;
  target_label: string | null;
  details: any;
  created_at: string;
  user_name: string | null;
  user_email: string | null;
}

interface DashboardSummary {
  // Decision 0006: open locations a notice can't reach, and notice outcomes.
  unreachable_locations?: { af_number: string; name: string | null; status: string; reason: string }[];
  missing_fbc?: { af_number: string; status: string; reason: string }[];
  assignments?: {
    people: { user_id: number; name: string | null; email: string; role: string; total: number; by_state: Record<string, number> }[];
    unassigned_after_notice: number;
  };
  notice_outcomes?: {
    notified: number; acknowledged: number; confirmed: number; confirmed_open: number; resolved: number;
    avg_days: { notified_to_acknowledged: number | null; to_confirmed: number | null; confirmed_to_resolved: number | null; notified_to_resolved: number | null };
  };
  last_run: { id: number; completed_at: string; pairs_evaluated: number; cases_opened: number } | null;
  weekly_stats: {
    opened_this_week: number;
    opened_prev_week: number;
    resolved_this_week: number;
    resolved_prev_week: number;
    notified_overdue: number;
  } | null;
  series_14d: { date: string; cases_opened: number; runs_complete: number; runs_failed: number }[] | null;
  recent_actions: RecentAction[];
}

// Who cases are assigned to, and in what state (Bettina, 2026-11-03).
const ASSIGN_STATES: [string, string][] = [
  ['NEW', 'New'], ['SURFACED', 'Reviewed'], ['NOTIFIED', 'Notified'], ['ACKD', 'Acknowledged'],
  ['CONFIRMED_VIOLATION', 'Confirmed violation'], ['RESOLVED', 'Resolved'], ['DISMISSED', 'Dismissed'],
  ['CLEARED_BY_EXCEPTION', 'Cleared by SPC/IR/FA'],
];
function AssignmentsCard({ a }: { a: NonNullable<DashboardSummary['assignments']> }) {
  // Only the state columns somebody actually has cases in.
  const cols = ASSIGN_STATES.filter(([k]) => a.people.some((p) => (p.by_state[k] ?? 0) > 0));
  // Grouped by type, FBCs first; alphabetical by first name within a group.
  const groups = ([['fbc', 'FBCs'], ['admin', 'Admins'], ['reviewer', 'Reviewers']] as const)
    .map(([role, label]) => ({
      label,
      people: a.people.filter((p) => p.role === role)
        .sort((x, y) => (x.name ?? x.email).localeCompare(y.name ?? y.email, undefined, { sensitivity: 'base' })),
    }))
    .filter((g) => g.people.length > 0);
  return (
    <div className="card">
      <div className="sec-head">
        <h3>Case assignments</h3>
        {a.unassigned_after_notice > 0 && (
          <span className="c-warn" style={{ fontSize: 12 }}>{a.unassigned_after_notice} notified-or-later case{a.unassigned_after_notice === 1 ? '' : 's'} unassigned</span>
        )}
      </div>
      {a.people.length === 0 ? (
        <div className="muted" style={{ padding: 16, fontSize: 12.5 }}>No cases are assigned yet.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Assigned to</th>
                <th style={{ textAlign: 'right' }}>Cases</th>
                {cols.map(([k, label]) => <th key={k} style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{label}</th>)}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => [
                <tr key={`h-${g.label}`}>
                  <td colSpan={cols.length + 2} className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', background: 'var(--surface-2)' }}>
                    {g.label} · {g.people.length}
                  </td>
                </tr>,
                ...g.people.map((p) => (
                <tr key={p.user_id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{p.name ?? p.email}</div>
                  </td>
                  <td className="num" style={{ textAlign: 'right', fontWeight: 600 }}>{p.total}</td>
                  {cols.map(([k]) => (
                    <td key={k} className="num" style={{ textAlign: 'right' }}>
                      {p.by_state[k] ? p.by_state[k] : <span className="faint">—</span>}
                    </td>
                  ))}
                </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ago(iso: string): string {
  const d = new Date(iso);
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function agoLong(iso: string): string {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h < 24) return r > 0 ? `${h}h ${r}m` : `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

function pctDelta(now: number | undefined, prev: number | undefined): string | null {
  if (now == null || prev == null) return null;
  if (prev === 0) return now === 0 ? '0%' : `↑ ${now}`;
  const pct = Math.round(((now - prev) / prev) * 100);
  if (pct === 0) return 'flat';
  return `${pct > 0 ? '↑' : '↓'} ${Math.abs(pct)}%`;
}

const ACTION_LABELS: Record<string, string> = {
  'case.transition': 'updated case',
  'audit.trigger': 'triggered audit',
  'sync.franchisees': 'synced franchisees from Zoho',
  'sync.spc': 'synced SPCs from Zoho Sign',
  'user.create': 'invited user',
  'user.update': 'updated user',
  'impersonate.start': 'started impersonating',
  'impersonate.stop': 'stopped impersonating',
};

export function Dashboard({ cases, onOpen, onTrigger, onJumpToQueue }: DashboardProps) {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  useEffect(() => {
    api<DashboardSummary>(`/api/dashboard`)
      .then((d) => setSummary(d))
      .catch(() => { /* keep null */ });
  }, []);
  const recentActions: RecentAction[] = summary?.recent_actions ?? [];

  const newOrSurfaced = cases.filter(c => c.state === 'NEW' || c.state === 'SURFACED').length;
  const unreachable = summary?.unreachable_locations ?? [];
  const missingFbc = summary?.missing_fbc ?? [];
  const outcomes = summary?.notice_outcomes;
  const d = (x: number | null | undefined) => (x == null ? '—' : `${x} d`);
  const notified = cases.filter(c => c.state === 'NOTIFIED').length;
  const resolvedTotal = cases.filter(c => c.state === 'RESOLVED').length;

  // Real 14-day sparkline: cases opened per day — rendered only under the KPI
  // it measures. (The old sparkB/sparkC drew runs-complete / cases-opened
  // trendlines under the Notified, Resolved, and Revenue tiles, implying
  // per-day series we don't actually track.)
  const sparkA = (summary?.series_14d ?? []).map((d) => d.cases_opened);

  // Week-over-week deltas for KPI sub-text. Returns "↑ 12%" or "—".
  const ws = summary?.weekly_stats;
  const openedDelta = pctDelta(ws?.opened_this_week, ws?.opened_prev_week);
  const resolvedDelta = pctDelta(ws?.resolved_this_week, ws?.resolved_prev_week);
  const overdue = ws?.notified_overdue ?? 0;
  const lastRunAgo = summary?.last_run ? agoLong(summary.last_run.completed_at) : null;

  const top = [...cases].filter(c => c.flags.length > 0).slice(0, 6);

  return (
    <div className="scroll-y" style={{ flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Welcome back</div>
          <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em' }}>Encroachment audit</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            {summary?.last_run
              ? <>
                  Last audit run completed <span className="num" style={{ color: 'var(--text)' }}>{lastRunAgo}</span> ago ·
                  evaluated <span className="num" style={{ color: 'var(--text)' }}>{summary.last_run.pairs_evaluated.toLocaleString()}</span> (P × F) pairs ·
                  opened <span className="num" style={{ color: 'var(--text)' }}>{summary.last_run.cases_opened.toLocaleString()}</span> cases
                </>
              : <>No audit runs yet. Trigger one from the case queue or the audit page.</>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={onTrigger}><Icon name="play" size={12} /> Trigger audit</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <div className="kpi" onClick={onJumpToQueue}>
          <div className="kpi-label">NEW + REVIEWED</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div className="kpi-value">{newOrSurfaced}</div>
            <Sparkline data={sparkA} color="var(--accent)" />
          </div>
          <div className="kpi-sub">{openedDelta ? <span className={openedDelta.startsWith('↑') ? 'c-warn' : 'c-ok'}>{openedDelta}</span> : 'no prior'} vs prior week</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Notified · clock running</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div className="kpi-value c-danger">{notified}</div>
          </div>
          <div className="kpi-sub">{overdue > 0 ? `${overdue} past 14-day response window` : 'all within response window'}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Resolved · total</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <div className="kpi-value">{resolvedTotal}</div>
          </div>
          <div className="kpi-sub">{resolvedDelta ? <span className={resolvedDelta.startsWith('↑') ? 'c-ok' : 'c-warn'}>{resolvedDelta}</span> : 'no prior'} vs prior week</div>
        </div>
      </div>

      <UnreadFbcUpdates onOpenCase={onOpen} />

      {unreachable.length > 0 && (
        <div className="card card-pad" style={{ borderColor: 'color-mix(in oklch, var(--danger) 40%, var(--border))', background: 'var(--warn-soft)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13.5 }}>
            <Icon name="alert" size={14} /> {unreachable.length} open location{unreachable.length === 1 ? '' : 's'} with no Amada email address
          </div>
          <div className="muted" style={{ fontSize: 12, margin: '3px 0 8px' }}>
            Notices only go to @amadaseniorcare.com / @amadafranchise.com addresses. Cases involving these locations are held out of every send until the Zoho record is fixed and franchisees are re-synced.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 12.5 }}>
            {unreachable.map((u) => (
              <span key={u.af_number}><span className="mono" style={{ fontWeight: 600 }}>{u.af_number}</span>{u.name ? ` ${u.name}` : ''} <span className="muted">· {u.reason}</span></span>
            ))}
          </div>
        </div>
      )}

      {missingFbc.length > 0 && (
        <div className="card card-pad" style={{ borderColor: 'color-mix(in oklch, var(--warn) 45%, var(--border))', background: 'var(--warn-soft)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13.5 }}>
            <Icon name="alert" size={14} /> {missingFbc.length} open location{missingFbc.length === 1 ? '' : 's'} with no FBC in Zoho
          </div>
          <div className="muted" style={{ fontSize: 12, margin: '3px 0 8px' }}>
            Notices name the FBC and copy them. For these locations a notice would read "your FBC" and no FBC would be copied. Fix the FBC in Zoho, then re-sync franchisees.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 12.5 }}>
            {missingFbc.map((u) => (
              <span key={u.af_number}><span className="mono" style={{ fontWeight: 600 }}>{u.af_number}</span> <span className="muted">· {u.reason}</span></span>
            ))}
          </div>
        </div>
      )}

      {outcomes && (
        <div className="card card-pad">
          <div style={{ fontWeight: 600, fontSize: 13.5, marginBottom: 8 }}>Notice outcomes</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, fontSize: 12.5 }}>
            <div><div className="muted">Notified</div><div className="num" style={{ fontSize: 20, fontWeight: 600 }}>{outcomes.notified}</div></div>
            <div><div className="muted">Acknowledged</div><div className="num" style={{ fontSize: 20, fontWeight: 600 }}>{outcomes.acknowledged}</div><div className="muted">avg {d(outcomes.avg_days.notified_to_acknowledged)} after notice</div></div>
            <div><div className="muted">Confirmed violations</div><div className="num c-danger" style={{ fontSize: 20, fontWeight: 600 }}>{outcomes.confirmed}</div><div className="muted">{outcomes.confirmed_open} still open · avg {d(outcomes.avg_days.to_confirmed)} to confirm</div></div>
            <div><div className="muted">Resolved after notice</div><div className="num" style={{ fontSize: 20, fontWeight: 600 }}>{outcomes.resolved}</div><div className="muted">avg {d(outcomes.avg_days.notified_to_resolved)} from notice · {d(outcomes.avg_days.confirmed_to_resolved)} from confirmed</div></div>
          </div>
        </div>
      )}

      {summary?.assignments && <AssignmentsCard a={summary.assignments} />}

      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 12 }}>
        <div className="card">
          <div className="sec-head">
            <h3>Top flagged · needs attention</h3>
            <button className="btn btn-sm btn-ghost" onClick={onJumpToQueue}>View all <Icon name="chevright" size={12} /></button>
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th>Case</th><th>Client · AF#</th><th>Flags</th><th style={{ textAlign: 'right' }}>$ at risk</th><th>State</th>
              </tr>
            </thead>
            <tbody>
              {top.map(c => (
                <tr key={c.id} className="clickable" onClick={() => onOpen(c.id)}>
                  <td className="mono" style={{ fontSize: 12.5 }}>{c.id}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{c.patient}</div>
                    <div className="muted" style={{ fontSize: 11.5 }}>{c.franchiseeAf} · {c.location}</div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {c.flags.slice(0, 3).map(f => {
                        const ft = FLAG_TYPES[f];
                        return <span key={f} className={`pill flag-pill sev-${ft.sev}`}>
                          <span className={`pill-dot dot-${ft.sev}`}></span>{ft.label}
                        </span>;
                      })}
                      {c.flags.length > 3 && <span className="pill"><span className="pill-dot"></span>+{c.flags.length - 3}</span>}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }} className="num mono">{formatCentsShort(c.totalRevenueCents)}</td>
                  <td><StatusPill state={c.state} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="sec-head"><h3>Recent activity</h3></div>
          <div style={{ padding: '4px 0' }}>
            {recentActions.length === 0 ? (
              <div className="muted" style={{ padding: 16, fontSize: 12.5 }}>No activity yet.</div>
            ) : recentActions.map((a, i) => {
              const who = a.user_name || a.user_email || 'system';
              const isSystem = !a.user_name && !a.user_email;
              const what = ACTION_LABELS[a.action] ?? a.action;
              const targetId = a.target_type && a.target_id ? `${a.target_type.toUpperCase()}-${a.target_id}` : '';
              const target = a.target_label
                ? <><span className="mono" style={{ fontSize: 11.5 }}>{targetId}</span> <span className="muted"> · {a.target_label}</span></>
                : (targetId ? <span className="mono" style={{ fontSize: 12 }}>{targetId}</span> : null);
              const meta = a.details && typeof a.details === 'object'
                ? (a.details.from && a.details.to ? `${a.details.from} → ${a.details.to}`
                   : a.details.cases_opened != null ? `${a.details.pairs_evaluated ?? '?'} pairs · ${a.details.cases_opened} cases opened`
                   : a.details.upserted != null ? `${a.details.upserted} upserted`
                   : null)
                : null;
              return (
                <div key={a.id} style={{ display: 'flex', gap: 10, padding: '10px 16px', borderBottom: i < recentActions.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div className="avatar" style={{ width: 24, height: 24, fontSize: 10, background: isSystem ? 'var(--surface-3)' : 'var(--accent-soft)', color: isSystem ? 'var(--text-muted)' : 'var(--accent)' }}>
                    {isSystem ? <Icon name="server" size={12} /> : who.split(/\s|@/).map(p => p[0]).join('').slice(0,2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.4 }}>
                    <div>
                      <span style={{ fontWeight: 500 }}>{who}</span>
                      <span className="muted"> {what} </span>
                      {target}
                    </div>
                    {meta && <div className="muted" style={{ fontSize: 11.5 }}>{meta}</div>}
                  </div>
                  <div className="faint" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{ago(a.created_at)}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="sec-head">
          <h3>Audit runs · last 14 days</h3>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', fontSize: 11.5 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--ok)' }} />Clean</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--gold)' }} />Flags found</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--danger)' }} />Critical</span>
          </div>
        </div>
        <div style={{ padding: 16 }}>
          <RunTimeline series={summary?.series_14d ?? null} />
        </div>
      </div>
    </div>
  );
}
